import React, { useEffect, useRef, useState } from "react";
import { Camera, RefreshCw, Check, Upload, X } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

/**
 * CameraCapture
 * - Opens user camera (rear-facing if available)
 * - Captures still frame to data URL
 * - Allows retake or use
 * - Falls back to file upload
 */
const CameraCapture = ({ onCapture, onCancel, initialImage }) => {
  const { t } = useLanguage();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState("");
  const [shot, setShot] = useState(initialImage || null);
  const [ready, setReady] = useState(false);

  const startCamera = async () => {
    setError("");
    setShot(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setReady(true);
      }
    } catch (e) {
      setError(t("id_grant"));
      setReady(false);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((tr) => tr.stop());
      streamRef.current = null;
    }
  };

  useEffect(() => {
    startCamera();
    return () => stopCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const takePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const w = video.videoWidth || 1280;
    const h = video.videoHeight || 720;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, w, h);
    const data = canvas.toDataURL("image/jpeg", 0.85);
    setShot(data);
    stopCamera();
  };

  const retake = () => {
    setShot(null);
    startCamera();
  };

  const useShot = () => {
    if (!shot) return;
    onCapture && onCapture(shot);
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      setShot(reader.result);
      stopCamera();
    };
    reader.readAsDataURL(f);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4 rs-fade-up" data-testid="camera-modal">
      <div className="w-full max-w-3xl bg-black border border-white/20 text-white">
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/20">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/50">RS · ID Capture</div>
            <h3 className="font-[Chivo] font-bold text-lg">{t("id_title")}</h3>
          </div>
          <button
            type="button"
            onClick={() => {
              stopCamera();
              onCancel && onCancel();
            }}
            className="text-white/70 hover:text-white"
            data-testid="camera-close-btn"
            aria-label="close"
          >
            <X size={22} />
          </button>
        </div>

        <div className="p-5">
          <div className="relative w-full aspect-[1.586/1] border-4 border-dashed border-[var(--rs-primary)] bg-black overflow-hidden">
            {!shot && (
              <video
                ref={videoRef}
                playsInline
                muted
                className="w-full h-full object-cover"
                data-testid="camera-video"
              />
            )}
            {shot && (
              <img src={shot} alt="ID capture" className="w-full h-full object-cover" data-testid="camera-preview" />
            )}
            {!ready && !shot && (
              <div className="absolute inset-0 flex items-center justify-center text-white/70 text-sm uppercase tracking-wider">
                {error || t("id_camera_off")}
              </div>
            )}
            <div className="absolute top-3 left-3 text-[10px] tracking-[0.25em] uppercase text-[var(--rs-primary)]">
              ● REC · ID DOC
            </div>
          </div>

          <p className="text-white/60 text-xs uppercase tracking-[0.2em] mt-4 text-center">{t("id_sub")}</p>

          <div className="flex flex-wrap gap-3 justify-between mt-6">
            <label className="rs-btn-secondary !bg-transparent !text-white !border-white/40 hover:!bg-white/10 cursor-pointer" data-testid="camera-upload-label">
              <Upload size={16} className="mr-2" />
              {t("id_or_upload")}
              <input type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid="camera-upload-input" />
            </label>

            <div className="flex gap-3">
              {!shot && (
                <button
                  type="button"
                  onClick={takePhoto}
                  className="rs-btn-primary"
                  disabled={!ready}
                  data-testid="camera-capture-btn"
                >
                  <Camera size={18} className="mr-2" />
                  {t("btn_capture")}
                </button>
              )}
              {shot && (
                <>
                  <button type="button" onClick={retake} className="rs-btn-secondary !bg-transparent !text-white !border-white/40 hover:!bg-white/10" data-testid="camera-retake-btn">
                    <RefreshCw size={16} className="mr-2" />
                    {t("btn_retake")}
                  </button>
                  <button type="button" onClick={useShot} className="rs-btn-primary" data-testid="camera-use-btn">
                    <Check size={18} className="mr-2" />
                    {t("btn_use_photo")}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        <canvas ref={canvasRef} className="hidden" />
      </div>
    </div>
  );
};

export default CameraCapture;
